import { Feature } from '@/utils/constant';
import { hasFeature } from '@/services/featureService';
import { isProcessBillingServerEligible } from '@/pages/caseManagement/processBilling/processBillingServerUtils';
import { getProcessBillingServer, isProcessBillingNotFoundError } from '@/services/processBillingServerService';
import type { WorkOrderGetByIdResponse } from '@/types/workOrder.types';
import type { ProcessBillingDocumentResponse } from '@/types/processBillingServer.types';
import { convertISODateToDDMMYYYY, convertISOToTime, displayFullName, formatCurrency } from '@/lib/utils';
import { useQuery } from '@tanstack/react-query';
import { FileText } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DocumentListDialog } from '@/components/DocumentListDialog';
import type { DocumentListType, MediaBaseResponse } from '@/types/types';
import { SendAffidavitConfirmDialog } from '@/pages/caseManagement/processBilling/SendAffidavitConfirmDialog';

export const ProcessBillingPePanel = ({ workOrder }: { workOrder: WorkOrderGetByIdResponse | undefined }) => {
  const navigate = useNavigate();
  const workOrderId = workOrder?.workOrderId;
  const clientId = workOrder?.service?.client?.clientId ?? null;

  const flagOn = hasFeature(Feature.PROCESS_BILLING_SERVER) || workOrder?.service?.type === 'PROCESS_SERVICE';
  const eligible = Boolean(workOrder && isProcessBillingServerEligible(workOrder));

  const processBillingServerQuery = useQuery({
    queryKey: ['processBillingServer', workOrderId],
    queryFn: () => getProcessBillingServer(workOrderId!),
    enabled: Boolean(workOrderId) && flagOn && eligible,
    retry: false,
  });

  const allAffidavits = useMemo<ProcessBillingDocumentResponse[]>(() => {
    if (!processBillingServerQuery.data) return [];
    return processBillingServerQuery.data.billingEntries.flatMap((entry) => entry.proofOfService);
  }, [processBillingServerQuery.data]);

  const allDocuments = useMemo<DocumentListType[]>(() => {
    return allAffidavits.map((doc) => ({
      name: doc.name,
      media: { mediaId: doc.mediaId } as MediaBaseResponse,
      affidavitSendDocumentId: doc.processBillingDocumentId,
    }));
  }, [allAffidavits]);

  const [openAffidavitDialog, setOpenAffidavitDialog] = useState(false);
  const [pendingSendAffidavitDocumentId, setPendingSendAffidavitDocumentId] = useState<string | null>(null);

  if (!flagOn || !eligible || !workOrderId) return null;

  const goView = () => navigate(`/process/workorder/${workOrderId}/process-billing`);

  if (processBillingServerQuery.isLoading) {
    return (
      <div className="mx-6 mb-4 rounded-lg bg-[#F5F5F5] p-4 text-sm text-placeholder border border-bordercolor">
        Loading process billing…
      </div>
    );
  }

  if (processBillingServerQuery.isError && isProcessBillingNotFoundError(processBillingServerQuery.error)) return null;
  if (processBillingServerQuery.isError) return null;
  if (!processBillingServerQuery.data) return null;
  if (processBillingServerQuery.data.processBillingBatchId === null) return null;

  const total = processBillingServerQuery.data.billingEntries.reduce((sum, e) => sum + (e.total ?? 0), 0);
  const updatedAt = processBillingServerQuery.data.updatedAt
    ? `${convertISODateToDDMMYYYY(processBillingServerQuery.data.updatedAt)} ${convertISOToTime(processBillingServerQuery.data.updatedAt)}`
    : '—';
  const updatedByName = displayFullName(processBillingServerQuery.data.updatedBy ?? {});
  const updatedLine = `Updated: ${updatedAt}${updatedByName && updatedByName !== '-' ? ` by ${updatedByName}` : ''}`;

  return (
    <div className="px-6 pt-2 pb-4 flex flex-col gap-3">
      <div
        className="flex flex-row gap-3 rounded-lg bg-[#F5F5F5] p-4 cursor-pointer items-start"
        onClick={goView}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            goView();
          }
        }}
      >
        <FileText className="w-6 h-6 text-primary shrink-0" />
        <div className="flex flex-col gap-1 flex-1 min-w-0">
          <span className="text-sm font-semibold text-primary">Billing Entry - Server Payment</span>
          <span className="text-xs text-placeholder">{updatedLine}</span>
          <div className="text-xs">
            <span className="text-placeholder">Amount: </span>
            <span className="text-primary font-medium">{formatCurrency(total)}</span>
          </div>
        </div>
      </div>

      {allAffidavits.length > 0 ? (
        <div
          className="flex flex-row gap-3 rounded-lg bg-[#F5F5F5] p-4 items-start cursor-pointer"
          onClick={() => setOpenAffidavitDialog(true)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setOpenAffidavitDialog(true);
            }
          }}
        >
          <FileText className="w-6 h-6 text-primary shrink-0" />
          <div className="flex flex-col gap-1 flex-1 min-w-0">
            <span className="text-sm font-semibold text-primary">Billing Entry - Affidavit</span>
            <span className="text-xs text-placeholder">{updatedLine}</span>
          </div>
        </div>
      ) : null}

      {openAffidavitDialog && allDocuments.length > 0 ? (
        <DocumentListDialog
          documents={allDocuments}
          activeIndex={0}
          onCloseClick={() => setOpenAffidavitDialog(false)}
          onRequestSendAffidavit={(id) => setPendingSendAffidavitDocumentId(id)}
        />
      ) : null}
      {pendingSendAffidavitDocumentId ? (
        <SendAffidavitConfirmDialog
          workOrderId={workOrderId}
          documentIds={[pendingSendAffidavitDocumentId]}
          clientId={clientId}
          onClose={() => setPendingSendAffidavitDocumentId(null)}
        />
      ) : null}
    </div>
  );
};
